import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import axiosProtected from "../../../../../api/config/baseWithToken";
import useWindowWidth from "../../../../../hooks/useWindowWidth";
import { factoryPictures, isVideo, productPictures, qualityStatus } from "./media";
import styles from "./qualityCheck.module.css";

// One picture shown whole, in a frame the same size on both sides. A blurred
// copy of the picture fills the space a portrait or wide shot leaves open.
function Media({ item, alt }) {
  if (isVideo(item)) {
    return <video className={styles.stageMedia} src={item.url} controls playsInline />;
  }
  return (
    <>
      <img className={styles.backdrop} src={item.url} alt="" aria-hidden="true" />
      <img className={styles.stageMedia} src={item.url} alt={alt} />
    </>
  );
}

function Pane({ title, items, emptyText }) {
  const [active, setActive] = useState(0);
  const current = items[active];
  return (
    <section className={styles.pane} aria-label={title}>
      <div className={styles.stage}>
        {current ? <Media item={current} alt={current.label || title} /> : <div className={styles.empty}>{emptyText}</div>}
        <div className={styles.glass}>
          <strong>{title}</strong>
          {current?.label && <span>{current.label}</span>}
          {items.length > 1 && (
            <span className={styles.count}>
              {active + 1}/{items.length}
            </span>
          )}
        </div>
      </div>
      {items.length > 1 && (
        <div className={styles.thumbs}>
          {items.map((item, i) => (
            <button
              key={item.url}
              type="button"
              className={i === active ? `${styles.thumb} ${styles.thumbOn}` : styles.thumb}
              onClick={() => setActive(i)}
              aria-label={item.label || `Picture ${i + 1}`}
              aria-pressed={i === active}
            >
              {isVideo(item) ? <span className={styles.play}>▶</span> : <img src={item.url} alt="" />}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// Side by side: the merchant's own pictures left, the factory's right, and a
// comment that goes to the team as a requote request.
export default function CompareDialog({ product, onClose, onRequested }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const phone = useWindowWidth() < 760;

  useEffect(() => setComment(""), [product?._id]);

  if (!product) return null;
  const status = qualityStatus(product);
  const history = product.quality_check_comments || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axiosProtected.post(`/products/${product._id}/quality-check-requote`, {
        comment: comment.trim(),
      });
      toast.success(data?.message || "Requote requested");
      setComment("");
      onRequested(data?.product);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not send your comment, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullScreen={phone} maxWidth="xl" fullWidth PaperProps={{ className: styles.dialog }}>
      <div className={styles.dialogHead}>
        <div>
          <h2>{product.title}</h2>
          <span className={`${styles.status} ${styles[status.key]}`}>{status.label}</span>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className={styles.compare}>
        <Pane title="Merchant listing" items={productPictures(product)} emptyText="No product pictures" />
        <Pane
          title="Supplier photos"
          items={factoryPictures(product)}
          emptyText="We're taking the pictures. This usually takes up to 48 hours."
        />
      </div>

      <form className={styles.comment} onSubmit={submit}>
        {history.length > 0 && (
          <ul className={styles.history}>
            {history.map((c) => (
              <li key={`${c.created_at}-${c.message.slice(0, 12)}`}>
                <time>{new Date(c.created_at).toLocaleDateString()}</time>
                {c.message}
              </li>
            ))}
          </ul>
        )}
        <label htmlFor="qc-comment">Something off? Tell us what and we'll check if we are able to re-quote.</label>
        <textarea
          id="qc-comment"
          rows={3}
          maxLength={2000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. The logo on the Blue / M variant should be centered"
        />
        <div className={styles.commentActions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Close
          </button>
          <button type="submit" className={styles.primary} disabled={!comment.trim() || busy}>
            {busy ? "Sending…" : "Request requote"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
